import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PrescribedItem } from '../../../../types'
import { selectPatient } from '../../../../features/patient/PatientContextSlice'
import type { RootState } from '../../../../app/store'

export interface DeliveryState {
  sendViaEmail: boolean
  sendViaSMS: boolean
  printPaperToken: boolean
  recipientEmail: string
  recipientMobile: string
}

export interface ScriptState extends DeliveryState {
  prescribedItemList: (PrescribedItem | undefined)[]
  selectedPrescribedItemIndex: number
  addPrescribedItemError?: string
  isPaperPrescription: boolean
}

const blankScript: ScriptState = {
  prescribedItemList: [undefined],
  selectedPrescribedItemIndex: 0,
  isPaperPrescription: false,
  sendViaEmail: false,
  sendViaSMS: false,
  printPaperToken: true,
  recipientEmail: '',
  recipientMobile: '',
}

export const ScriptSlice = createSlice({
  name: 'newScript',
  initialState: blankScript,
  reducers: {
    fetchPrescription: (state, { payload }: PayloadAction<PrescribedItem[]>) => {
      state.prescribedItemList = payload
    },
    addPrescribedItem: (
      state,
      { payload: newPrescribedItem }: PayloadAction<PrescribedItem | undefined>
    ) => {
      state.prescribedItemList.push(newPrescribedItem)
      state.selectedPrescribedItemIndex = state.prescribedItemList.length - 1
    },
    selectPrescribedItem: (state, { payload: index }: PayloadAction<number>) => {
      state.selectedPrescribedItemIndex = index
    },
    updatePrescribedItem: (
      state,
      {
        payload: { index, updatedItem },
      }: PayloadAction<{
        index: number
        updatedItem: PrescribedItem
      }>
    ) => {
      state.prescribedItemList[index] = updatedItem
    },
    deletePrescribedItem: (state, { payload: index }: PayloadAction<number>) => {
      state.prescribedItemList.splice(index, 1)
    },
    resetPrescribedItemByIndex: (state, { payload: index }: PayloadAction<number>) => {
      state.prescribedItemList[index] = undefined
    },
    cleanPrescribedItems: (state) => {
      const validPrescribedItems = state.prescribedItemList.filter((item) => item !== undefined)
      state.prescribedItemList =
        validPrescribedItems.length > 0 ? validPrescribedItems : [undefined]
      state.selectedPrescribedItemIndex = 0
    },
    setDeliveryState: (state, { payload }: PayloadAction<Partial<DeliveryState>>) => {
      return { ...state, ...payload }
    },
    setScriptState: (state, { payload }: PayloadAction<Partial<ScriptState>>) => {
      return { ...state, ...payload }
    },
    resetScript: () => {
      // Return a fresh copy of blankScript to avoid mutating the shared reference
      return {
        ...blankScript,
        prescribedItemList: [undefined],
      }
    },
    setIsPaperPrescription: (state, { payload: isPaperPrescription }: PayloadAction<boolean>) => {
      state.isPaperPrescription = isPaperPrescription
    },
    uncheckIsPaperPrescription: (state) => {
      state.isPaperPrescription = false
    },
    setAddPrescribedItemError: (state, { payload }: PayloadAction<string | undefined>) => {
      state.addPrescribedItemError = payload
    },
  },
  extraReducers(builder) {
    builder.addCase(selectPatient, (state, { payload: patientDetails }) => {
      state.isPaperPrescription = patientDetails?.paper_only ?? false
    })
  },
})

export const {
  fetchPrescription,
  addPrescribedItem,
  selectPrescribedItem,
  updatePrescribedItem,
  deletePrescribedItem,
  resetPrescribedItemByIndex,
  setDeliveryState,
  setScriptState,
  resetScript,
  setIsPaperPrescription,
  uncheckIsPaperPrescription,
  setAddPrescribedItemError,
  cleanPrescribedItems,
} = ScriptSlice.actions

export default ScriptSlice.reducer

export const definedPrescribedItemsSelector = createSelector(
  [(state: RootState) => state.newScript.prescribedItemList],
  (items): PrescribedItem[] => items.filter((item): item is PrescribedItem => item !== undefined)
)
